import { useState } from "react";
import { Calculator, CheckCircle2, AlertTriangle } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";

export default function ConsistencyCalculator() {
  const [total, setTotal] = useState("");
  const [best, setBest] = useState("");

  const t = parseFloat(total) || 0;
  const b = parseFloat(best) || 0;
  const valid = t > 0 && b > 0 && b <= t;
  const pct = valid ? (b / t) * 100 : null;
  const eligible = pct !== null && pct <= 25;
  const needed = pct !== null && !eligible ? Math.ceil(b / 0.25 - t) : 0;

  return (
    <section id="calculator" className="py-24 lg:py-32 bg-[#030B16]/40" data-testid="consistency-calculator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Chapter index={4} label="Consistency Check" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.04em] text-white leading-[1.15]">
            The 25% rule, <span className="gold-text">decoded.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#999999] leading-relaxed max-w-lg">
            Instant Funding Light payouts follow one simple rule: no single trading day may account
            for more than 25% of your total profits. Test your numbers before you request a payout.
          </p>
          <div className="mt-8 glass-card rounded-xl p-5 max-w-lg">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-[#FFD700] mb-2">
              The Formula
            </div>
            <code className="text-sm text-[#DBDFE8]">
              Consistency % = (Best Day Profit ÷ Total Profit) × 100
            </code>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass-card rounded-2xl p-6 sm:p-8 border-[#FFD700]/20">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-10 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/25 flex items-center justify-center">
                <Calculator size={18} className="text-[#FFD700]" />
              </div>
              <h3 className="text-lg font-semibold tracking-[-0.02em] text-white">
                Consistency Calculator
              </h3>
            </div>

            <div className="space-y-5">
              <div>
                <label htmlFor="calc-total" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#999999] block mb-2">
                  Total Profit Earned ($)
                </label>
                <input
                  id="calc-total"
                  data-testid="calc-input-total-profit"
                  type="number"
                  min="0"
                  value={total}
                  onChange={(e) => setTotal(e.target.value)}
                  placeholder="e.g. 20000"
                  className="w-full rounded-xl bg-[#030B16]/70 border border-white/10 px-4 py-3.5 text-white text-lg font-semibold placeholder:text-[#999999]/40 placeholder:font-normal focus:outline-none focus:border-[#FFD700]/60 focus:ring-1 focus:ring-[#FFD700]/30 transition-all"
                />
              </div>
              <div>
                <label htmlFor="calc-best" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#999999] block mb-2">
                  Highest Single Day Profit ($)
                </label>
                <input
                  id="calc-best"
                  data-testid="calc-input-best-day-profit"
                  type="number"
                  min="0"
                  value={best}
                  onChange={(e) => setBest(e.target.value)}
                  placeholder="e.g. 5000"
                  className="w-full rounded-xl bg-[#030B16]/70 border border-white/10 px-4 py-3.5 text-white text-lg font-semibold placeholder:text-[#999999]/40 placeholder:font-normal focus:outline-none focus:border-[#FFD700]/60 focus:ring-1 focus:ring-[#FFD700]/30 transition-all"
                />
              </div>
            </div>

            <div className="mt-7 rounded-xl bg-[#0D0E13]/80 border border-white/10 p-5">
              {pct === null ? (
                <p className="text-sm text-[#999999]" data-testid="calc-result-status-badge">
                  {b > t && b > 0 && t > 0
                    ? "Best day profit can't exceed total profit — check your numbers."
                    : "Enter your numbers to check payout eligibility."}
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-[#999999]">Consistency</span>
                    <span
                      data-testid="calc-result-consistency-percent"
                      className={`text-3xl font-extrabold tracking-[-0.03em] ${eligible ? "text-[#22C55E]" : "text-[#EF4444]"}`}
                    >
                      {pct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-4">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${eligible ? "bg-[#22C55E]" : "bg-[#EF4444]"}`}
                      style={{ width: `${Math.min(pct / 50, 1) * 100}%` }}
                    />
                  </div>
                  <div
                    data-testid="calc-result-status-badge"
                    className={`flex items-start gap-2.5 text-sm font-medium ${eligible ? "text-[#22C55E]" : "text-[#EF4444]"}`}
                  >
                    {eligible ? (
                      <>
                        <CheckCircle2 size={17} className="mt-0.5 shrink-0" />
                        <span>You're within the 25% threshold — clear to request your payout.</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={17} className="mt-0.5 shrink-0" />
                        <span>
                          Above the 25% limit. Earn <strong>${needed.toLocaleString()}</strong> more
                          in total profit before requesting a payout.
                        </span>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
